// ActivityViews — shell that routes between activity views.
//
// Activity views (Saved, Unreads, future Mentions / Threads overview)
// share the right-pane real estate with the room timeline. The shell
// reads `activeView` and renders the matching view; the room timeline
// is rendered by the caller when `activeView === "room"`.
//
// Keeping the routing here (rather than chaining `if/else` at every
// caller) means future activity views slot in without touching App.tsx
// or EmbeddableChat — both call `<ActivityViews />` and let this
// component decide.

import { type ReactNode } from "react";

import { ArchivedRoomsView } from "~/components/ArchivedRoomsView";
import { MentionsView } from "~/components/MentionsView";
import { SavedView } from "~/components/SavedView";
import { ScheduledQueue } from "~/components/ScheduledQueue";
import { ThreadsView } from "~/components/ThreadsView";
import { UnreadsView } from "~/components/UnreadsView";
import { useMatrix } from "~/context/MatrixContext";
import { useRooms } from "~/hooks/useRooms";

/**
 * Returns the active activity view, or `null` when the active view is
 * `"room"`. Callers branch on `null` to render the room timeline
 * instead — the activity-view set is closed (Saved + Unreads today),
 * which keeps the type narrow and avoids a fall-through default that
 * would silently render the wrong surface for a future view.
 */
export function ActivityViews(): ReactNode {
  const { activeView } = useMatrix();
  const rooms = useRooms();
  switch (activeView) {
    case "saved":
      return <SavedView />;
    case "unreads":
      return <UnreadsView />;
    case "mentions":
      return <MentionsView />;
    case "threads":
      return <ThreadsView />;
    case "scheduled":
      return <ScheduledQueue rooms={rooms} />;
    case "archived":
      return <ArchivedRoomsView />;
    case "room":
      return null;
  }
}
